import {Document, Page, StyleSheet, Text, View} from '@react-pdf/renderer'
import {TitleSummaryReportDocumentProps} from '../../../../entity/props/report/title-summary-report-document-props'
import {formatBoolean, formatCurrency, formatDate} from '../../../../shared/report/report-util'
import {Assignment} from '../../../../entity/resource/assignment';
import {Estate} from '../../../../entity/resource/estate';
import {MobileHome, MobileHomeTaxYear} from '../../../../entity/resource/mobile-home';
import {Mortgage} from '../../../../entity/resource/mortgage';
import {TaxYear} from '../../../../entity/resource/tax-year';
import {VestingDeed} from '../../../../entity/resource/vesting-deed';

const TitleSummaryReportDocument = (props: TitleSummaryReportDocumentProps): JSX.Element => {
    const hasVestingDeeds = props.titleSummaryRequest.vestingDeeds && props.titleSummaryRequest.vestingDeeds.length > 0;
    const hasEstates = props.titleSummaryRequest.estates && props.titleSummaryRequest.estates.length > 0;
    const hasTaxYears = props.titleSummaryRequest.taxYears && props.titleSummaryRequest.taxYears.length > 0;
    const hasMobileHomes = props.titleSummaryRequest.mobileHome && props.titleSummaryRequest.mobileHome.length > 0;
    const hasMortgages = props.titleSummaryRequest.mortgages && props.titleSummaryRequest.mortgages.length > 0;
    const hasJudgments = props.titleSummaryRequest.judgments && props.titleSummaryRequest.judgments.length > 0;
    const hasLiens = props.titleSummaryRequest.liens && props.titleSummaryRequest.liens.length > 0;
    const hasUCCs = props.titleSummaryRequest.uccs && props.titleSummaryRequest.uccs.length > 0;
    const hasRestrictions = props.titleSummaryRequest.restrictionsAgreements && props.titleSummaryRequest.restrictionsAgreements.length > 0;
    const hasEasements = props.titleSummaryRequest.easementsRightOfWays && props.titleSummaryRequest.easementsRightOfWays.length > 0;

    const styles = StyleSheet.create({
        page: {
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            padding: 40,
            fontSize: 10
        },
        header: {
            marginBottom: 30,
            borderBottom: '2px solid #2c3e50',
            paddingBottom: 15
        },
        title: {
            fontSize: 24,
            fontWeight: 'bold',
            color: '#2c3e50',
            textAlign: 'center',
            marginBottom: 10
        },
        subtitle: {
            fontSize: 14,
            color: '#7f8c8d',
            textAlign: 'center',
            marginBottom: 20
        },
        section: {
            marginBottom: 15,
            pageBreakInside: 'avoid',
            pageBreakBefore: 'auto'
        },
        sectionTitle: {
            fontSize: 16,
            fontWeight: 'bold',
            color: '#2c3e50',
            marginBottom: 10,
            backgroundColor: '#ecf0f1',
            padding: 8,
            borderRadius: 4,
            pageBreakAfter: 'avoid',
            pageBreakBefore: 'avoid',
            pageBreakInside: 'avoid'
        },
        row: {
            flexDirection: 'row',
            marginBottom: 8,
            alignItems: 'center'
        },
        label: {
            width: '30%',
            fontSize: 10,
            fontWeight: 'bold',
            color: '#34495e'
        },
        value: {
            width: '70%',
            fontSize: 10,
            color: '#2c3e50'
        },
        table: {
            marginTop: 10,
            borderWidth: 1,
            borderColor: '#bdc3c7',
            pageBreakInside: 'avoid'
        },
        tableHeader: {
            backgroundColor: '#34495e',
            flexDirection: 'row',
            padding: 8
        },
        tableHeaderCell: {
            flex: 1,
            fontSize: 9,
            fontWeight: 'bold',
            color: '#ffffff',
            textAlign: 'center'
        },
        tableRow: {
            flexDirection: 'row',
            borderTopWidth: 1,
            borderTopColor: '#bdc3c7',
            padding: 6
        },
        tableCell: {
            flex: 1,
            fontSize: 9,
            color: '#2c3e50',
            textAlign: 'center'
        },
        pageNumber: {
            position: 'absolute',
            bottom: 30,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 8,
            color: '#7f8c8d'
        },
        sectionContainer: {
            pageBreakInside: 'avoid',
            pageBreakBefore: 'auto',
            pageBreakAfter: 'avoid',
            marginBottom: 20
        },
        sectionWrapper: {
            pageBreakInside: 'avoid',
            pageBreakBefore: 'auto',
            pageBreakAfter: 'avoid',
            marginBottom: 25
        },
        noDataText: {
            fontSize: 12,
            color: '#7f8c8d',
            fontStyle: 'italic',
            textAlign: 'center',
            marginTop: 10,
            marginBottom: 10
        }
    })

    const renderMainPage = (): JSX.Element => (
        <Page size='A4' style={styles.page}>
            <View style={styles.header}>
                <Text style={styles.title}>Title Summary Report</Text>
                <Text style={styles.subtitle}>
                    Generated on {new Date().toLocaleDateString('en-US')}
                </Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.sectionTitle}>Order Information</Text>
                <View style={styles.row}>
                    <Text style={styles.label}>Order Number:</Text>
                    <Text style={styles.value}>{props.titleSummaryRequest.orderInformation?.orderNumber ?? 'N/A'}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.label}>Search Type:</Text>
                    <Text style={styles.value}>{props.titleSummaryRequest.orderInformation?.searchType ?? 'N/A'}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.label}>Search Start Date:</Text>
                    <Text style={styles.value}>{formatDate(props.titleSummaryRequest.orderInformation?.searchStartDate)}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.label}>Search End Date:</Text>
                    <Text style={styles.value}>{formatDate(props.titleSummaryRequest.orderInformation?.searchEndDate)}</Text>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.sectionTitle}>Property Information</Text>
                <View style={styles.row}>
                    <Text style={styles.label}>State:</Text>
                    <Text style={styles.value}>{props.state?.state ?? 'N/A'}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.label}>County:</Text>
                    <Text style={styles.value}>{props.county?.name ?? 'N/A'}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.label}>Address:</Text>
                    <Text style={styles.value}>{props.titleSummaryRequest.propertyInformation?.propertyAddress ?? 'N/A'}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.label}>Parcel Number:</Text>
                    <Text style={styles.value}>{props.titleSummaryRequest.propertyInformation?.parcelNumber ?? 'N/A'}</Text>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.sectionTitle}>Current Owner Information</Text>
                <View style={styles.row}>
                    <Text style={styles.label}>Current Owners:</Text>
                    <Text style={styles.value}>{props.titleSummaryRequest.propertyInformation?.currentOwner ?? 'N/A'}</Text>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.sectionTitle}>General Comments</Text>
                <View style={styles.section}>
                    <Text style={styles.value}>{props.titleSummaryRequest.comment ?? 'N/A'}</Text>
                </View>
            </View>

            <Text style={styles.pageNumber} render={({pageNumber, totalPages}) => `Page ${pageNumber} of ${totalPages}`} />
        </Page>
    )

    const renderVestingDeedsSection = (): JSX.Element => (
        <View style={styles.sectionWrapper}>
            <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>Vesting Deeds</Text>
                {
                    hasVestingDeeds
                        ? <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={styles.tableHeaderCell}>Recorded Date</Text>
                                <Text style={styles.tableHeaderCell}>Book</Text>
                                <Text style={styles.tableHeaderCell}>Page</Text>
                                <Text style={styles.tableHeaderCell}>Legal Description</Text>
                            </View>
                            {
                                props.titleSummaryRequest.vestingDeeds?.map((deed: VestingDeed, index: number) => (
                                    <View key={index} style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{formatDate(deed.recordedDate)}</Text>
                                        <Text style={styles.tableCell}>{deed.book ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{deed.page ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{deed.legalDescription ?? 'N/A'}</Text>
                                    </View>
                                ))
                            }
                          </View>
                        : <Text style={styles.noDataText}>No Data Found</Text>
                }
            </View>
        </View>
    )

    const renderEstatesSection = (): JSX.Element => (
        <View style={styles.sectionWrapper}>
            <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>Estates</Text>
                {
                    hasEstates
                        ? <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={styles.tableHeaderCell}>Estate Number</Text>
                                <Text style={styles.tableHeaderCell}>Decedent Name</Text>
                                <Text style={styles.tableHeaderCell}>Heirs Listed</Text>
                                <Text style={styles.tableHeaderCell}>Heirs Searched</Text>
                                <Text style={styles.tableHeaderCell}>Status</Text>
                            </View>
                            {
                                props.titleSummaryRequest.estates?.map((estate: Estate, index: number) => (
                                    <View key={index} style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{estate.estateNumber ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{estate.decedentName ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{formatBoolean(estate.heirsListed)}</Text>
                                        <Text style={styles.tableCell}>{formatBoolean(estate.heirsSearched)}</Text>
                                        <Text style={styles.tableCell}>{estate.estateStatus ?? 'N/A'}</Text>
                                    </View>
                                ))
                            }
                          </View>
                        : <Text style={styles.noDataText}>No Data Found</Text>
                }
            </View>
        </View>
    )

    const renderTaxSection = (): JSX.Element => (
        <View style={styles.sectionWrapper}>
            <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>Tax Information</Text>
                {
                    props.titleSummaryRequest.taxFlags
                        ? <>
                            <View style={styles.row}>
                                <Text style={styles.label}>Pending Tax Sale:</Text>
                                <Text style={styles.value}>{formatBoolean(props.titleSummaryRequest.taxFlags.pendingTaxSale)}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Delinquent Taxes:</Text>
                                <Text style={styles.value}>{formatBoolean(props.titleSummaryRequest.taxFlags.delinquentTaxes)}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Roll Back Taxes:</Text>
                                <Text style={styles.value}>{formatBoolean(props.titleSummaryRequest.taxFlags.rollBackTaxes)}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Tax Exempt:</Text>
                                <Text style={styles.value}>{formatBoolean(props.titleSummaryRequest.taxFlags.taxExempt)}</Text>
                            </View>
                            {
                                hasTaxYears
                                    ? <View style={styles.table}>
                                        <View style={styles.tableHeader}>
                                            <Text style={styles.tableHeaderCell}>Year</Text>
                                            <Text style={styles.tableHeaderCell}>Amount Owed</Text>
                                            <Text style={styles.tableHeaderCell}>Amount Paid</Text>
                                        </View>
                                        {
                                            props.titleSummaryRequest.taxYears?.map((taxYear: TaxYear, index: number) => (
                                                <View key={index} style={styles.tableRow}>
                                                    <Text style={styles.tableCell}>{taxYear.year ?? 'N/A'}</Text>
                                                    <Text style={styles.tableCell}>{formatCurrency(taxYear.amountOwed)}</Text>
                                                    <Text style={styles.tableCell}>{formatCurrency(taxYear.amountPaid)}</Text>
                                                </View>
                                            ))
                                        }
                                      </View>
                                    : <Text style={styles.noDataText}>No Tax Years Data Found</Text>
                            }
                          </>
                        : <Text style={styles.noDataText}>No Data Found</Text>
                }
            </View>
        </View>
    )

    const renderJudgmentsSection = (): JSX.Element => (
        <View style={styles.sectionWrapper}>
            <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>Judgments</Text>
                {
                    hasJudgments
                        ? <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={styles.tableHeaderCell}>Plaintiff</Text>
                                <Text style={styles.tableHeaderCell}>Defendant</Text>
                                <Text style={styles.tableHeaderCell}>Amount</Text>
                                <Text style={styles.tableHeaderCell}>Recorded</Text>
                                <Text style={styles.tableHeaderCell}>Book</Text>
                                <Text style={styles.tableHeaderCell}>Page</Text>
                                <Text style={styles.tableHeaderCell}>Description</Text>
                            </View>
                            {
                                props.titleSummaryRequest.judgments?.map((judgment, index) => (
                                    <View key={index} style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{judgment.plaintiff ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{judgment.defendant ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{formatCurrency(judgment.amount)}</Text>
                                        <Text style={styles.tableCell}>{formatDate(judgment.recordedDate)}</Text>
                                        <Text style={styles.tableCell}>{judgment.book ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{judgment.page ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{judgment.description ?? 'N/A'}</Text>
                                    </View>
                                ))
                            }
                          </View>
                        : <Text style={styles.noDataText}>No Data Found</Text>
                }
            </View>
        </View>
    )

    const renderLiensSection = (): JSX.Element => (
        <View style={styles.sectionWrapper}>
            <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>Liens</Text>
                {
                    hasLiens
                        ? <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={styles.tableHeaderCell}>Type</Text>
                                <Text style={styles.tableHeaderCell}>Amount</Text>
                                <Text style={styles.tableHeaderCell}>Recorded</Text>
                                <Text style={styles.tableHeaderCell}>Book</Text>
                                <Text style={styles.tableHeaderCell}>Page</Text>
                                <Text style={styles.tableHeaderCell}>Description</Text>
                            </View>
                            {
                                props.titleSummaryRequest.liens?.map((lien, index) => (
                                    <View key={index} style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{lien.type ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{formatCurrency(lien.amount)}</Text>
                                        <Text style={styles.tableCell}>{formatDate(lien.recordedDate)}</Text>
                                        <Text style={styles.tableCell}>{lien.book ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{lien.page ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{lien.description ?? 'N/A'}</Text>
                                    </View>
                                ))
                            }
                          </View>
                        : <Text style={styles.noDataText}>No Data Found</Text>
                }
            </View>
        </View>
    )

    const renderUCCsSection = (): JSX.Element => (
        <View style={styles.sectionWrapper}>
            <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>UCC Filings</Text>
                {
                    hasUCCs
                        ? <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={styles.tableHeaderCell}>Debtor</Text>
                                <Text style={styles.tableHeaderCell}>Secured Party</Text>
                                <Text style={styles.tableHeaderCell}>Filing Date</Text>
                                <Text style={styles.tableHeaderCell}>Filing Number</Text>
                                <Text style={styles.tableHeaderCell}>Description</Text>
                            </View>
                            {
                                props.titleSummaryRequest.uccs?.map((ucc, index) => (
                                    <View key={index} style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{ucc.debtor ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{ucc.securedParty ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{formatDate(ucc.filingDate)}</Text>
                                        <Text style={styles.tableCell}>{ucc.filingNumber ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{ucc.description ?? 'N/A'}</Text>
                                    </View>
                                ))
                            }
                          </View>
                        : <Text style={styles.noDataText}>No Data Found</Text>
                }
            </View>
        </View>
    )

    const renderRestrictionsSection = (): JSX.Element => (
        <View style={styles.sectionWrapper}>
            <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>Restrictions</Text>
                {
                    hasRestrictions
                        ? <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={styles.tableHeaderCell}>Book</Text>
                                <Text style={styles.tableHeaderCell}>Page</Text>
                                <Text style={styles.tableHeaderCell}>Recorded Date</Text>
                                <Text style={styles.tableHeaderCell}>Title</Text>
                                <Text style={styles.tableHeaderCell}>Description</Text>
                            </View>
                            {
                                props.titleSummaryRequest.restrictionsAgreements?.map((restriction, index) => (
                                    <View key={index} style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{restriction.book ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{restriction.page ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{formatDate(restriction.recordedDate)}</Text>
                                        <Text style={styles.tableCell}>{restriction.title ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{restriction.description ?? 'N/A'}</Text>
                                    </View>
                                ))
                            }
                          </View>
                        : <Text style={styles.noDataText}>No Data Found</Text>
                }
            </View>
        </View>
    )

    const renderEasementsSection = (): JSX.Element => (
        <View style={styles.sectionWrapper}>
            <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>Easements</Text>
                {
                    hasEasements
                        ? <View style={styles.table}>
                            <View style={styles.tableHeader}>
                                <Text style={styles.tableHeaderCell}>Book</Text>
                                <Text style={styles.tableHeaderCell}>Page</Text>
                                <Text style={styles.tableHeaderCell}>Recorded Date</Text>
                                <Text style={styles.tableHeaderCell}>Type</Text>
                                <Text style={styles.tableHeaderCell}>Title</Text>
                                <Text style={styles.tableHeaderCell}>Description</Text>
                            </View>
                            {
                                props.titleSummaryRequest.easementsRightOfWays?.map((easement, index) => (
                                    <View key={index} style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{easement.book ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{easement.page ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{formatDate(easement.recordedDate)}</Text>
                                        <Text style={styles.tableCell}>{easement.type ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{easement.title ?? 'N/A'}</Text>
                                        <Text style={styles.tableCell}>{easement.description ?? 'N/A'}</Text>
                                    </View>
                                ))
                            }
                          </View>
                        : <Text style={styles.noDataText}>No Data Found</Text>
                }
            </View>
        </View>
    )

    const renderAdditionalPages = (): JSX.Element => {
        const pages: JSX.Element[] = []

        const calculateVestingDeedsHeight = (): number => {
            if (!hasVestingDeeds) return 120;

            const baseHeight = 120;
            const rowHeight = 25;
            const totalRows = props.titleSummaryRequest.vestingDeeds?.length ?? 0;

            return baseHeight + (totalRows * rowHeight);
        }

        const calculateEstatesHeight = (): number => {
            if (!hasEstates) return 120;

            const sectionTitleHeight = 50;
            const tableHeaderHeight = 25;
            const rowHeight = 25;
            const totalRows = props.titleSummaryRequest.estates?.length ?? 0;

            return sectionTitleHeight + tableHeaderHeight + (totalRows * rowHeight);
        }

        const calculateTaxHeight = (): number => {
            if (!props.titleSummaryRequest.taxFlags) return 120;

            const sectionTitleHeight = 50;
            const taxFlagsHeight = 100;
            const tableHeaderHeight = 25;
            const rowHeight = 25;
            const totalRows = props.titleSummaryRequest.taxYears?.length ?? 0;

            return sectionTitleHeight + taxFlagsHeight + tableHeaderHeight + (totalRows * rowHeight);
        }

        const calculateMobileHomesHeight = (): number => {
            if (!hasMobileHomes) return 120;

            let totalHeight = 150;
            props.titleSummaryRequest.mobileHome?.forEach((mobileHome: MobileHome): void => {
                totalHeight += calculateIndividualMobileHomeHeight(mobileHome);
            });

            return totalHeight;
        }

        const calculateIndividualMobileHomeHeight = (mobileHome: MobileHome): number => {
            let height = 120;

            if (mobileHome.lienRetirement)
                height += 80;

            if (mobileHome.taxYears && mobileHome.taxYears.length > 0) {
                height += 80;
                height += (mobileHome.taxYears.length * 25);
            }

            return height;
        }

        const calculateMortgagesHeight = (): number => {
            if (!hasMortgages) return 120;

            let totalHeight = 150;
            props.titleSummaryRequest.mortgages?.forEach((mortgage: Mortgage): void => {
                totalHeight += calculateIndividualMortgageHeight(mortgage);
            });

            return totalHeight;
        }

        const calculateIndividualMortgageHeight = (mortgage: Mortgage): number => {
            let height = 120;

            if (mortgage.assignments && mortgage.assignments.length > 0) {
                height += 80;
                height += (mortgage.assignments.length * 25);
            }

            return height;
        }

        const calculateJudgmentsHeight = (): number => {
            if (!hasJudgments) return 120;

            const baseHeight = 120;
            const rowHeight = 25;
            const totalRows = props.titleSummaryRequest.judgments?.length ?? 0;

            return baseHeight + (totalRows * rowHeight);
        }

        const calculateLiensHeight = (): number => {
            if (!hasLiens) return 120;

            const baseHeight = 120;
            const rowHeight = 25;
            const totalRows = props.titleSummaryRequest.liens?.length ?? 0;

            return baseHeight + (totalRows * rowHeight);
        }

        const calculateUCCsHeight = (): number => {
            if (!hasUCCs) return 120;

            const baseHeight = 120;
            const rowHeight = 25;
            const totalRows = props.titleSummaryRequest.uccs?.length ?? 0;

            return baseHeight + (totalRows * rowHeight);
        }

        const calculateRestrictionsHeight = (): number => {
            if (!hasRestrictions) return 120;

            const baseHeight = 120;
            const rowHeight = 25;
            const totalRows = props.titleSummaryRequest.restrictionsAgreements?.length ?? 0;

            return baseHeight + (totalRows * rowHeight);
        }

        const calculateEasementsHeight = (): number => {
            if (!hasEasements) return 120;

            const baseHeight = 120;
            const rowHeight = 25;
            const totalRows = props.titleSummaryRequest.easementsRightOfWays?.length ?? 0;

            return baseHeight + (totalRows * rowHeight);
        }

        const sectionsToRender: {key: string, section: JSX.Element, estimatedHeight: number}[] = [];

        sectionsToRender.push({
            key: 'vestingDeeds',
            section: renderVestingDeedsSection(),
            estimatedHeight: calculateVestingDeedsHeight()
        });

        sectionsToRender.push({
            key: 'estates',
            section: renderEstatesSection(),
            estimatedHeight: calculateEstatesHeight()
        });

        sectionsToRender.push({
            key: 'tax',
            section: renderTaxSection(),
            estimatedHeight: calculateTaxHeight()
        });

        sectionsToRender.push({
            key: 'mobileHomes',
            section: <></>,
            estimatedHeight: calculateMobileHomesHeight()
        });

        sectionsToRender.push({
            key: 'mortgages',
            section: <></>,
            estimatedHeight: calculateMortgagesHeight()
        });

        sectionsToRender.push({
            key: 'judgments',
            section: renderJudgmentsSection(),
            estimatedHeight: calculateJudgmentsHeight()
        });

        sectionsToRender.push({
            key: 'liens',
            section: renderLiensSection(),
            estimatedHeight: calculateLiensHeight()
        });

        sectionsToRender.push({
            key: 'uccs',
            section: renderUCCsSection(),
            estimatedHeight: calculateUCCsHeight()
        });

        sectionsToRender.push({
            key: 'restrictions',
            section: renderRestrictionsSection(),
            estimatedHeight: calculateRestrictionsHeight()
        });

        sectionsToRender.push({
            key: 'easements',
            section: renderEasementsSection(),
            estimatedHeight: calculateEasementsHeight()
        });

        let currentPageSections: JSX.Element[] = [];
        let currentPageHeight = 0;
        const pageBreakThreshold = 600;

        sectionsToRender.forEach((sectionData) => {
            const {section, estimatedHeight, key} = sectionData;

            if (key === 'mobileHomes' && hasMobileHomes) {
                const mobileHomes = props.titleSummaryRequest.mobileHome ?? [];
                const sectionTitleHeight = 50;
                let mobileHomeSections: JSX.Element[] = [];
                let currentPageHeightWithSection = currentPageHeight + sectionTitleHeight;
                let isFirstMobileHomePage = true;

                mobileHomes.forEach((mobileHome, index) => {
                    const individualHeight = calculateIndividualMobileHomeHeight(mobileHome);
                    
                    if (currentPageHeightWithSection + individualHeight > pageBreakThreshold) {
                        if (mobileHomeSections.length > 0) 
                            currentPageSections.push(
                                <View style={styles.sectionWrapper}>
                                    <View style={styles.sectionContainer}>
                                        {isFirstMobileHomePage && <Text style={styles.sectionTitle}>Mobile Homes</Text>}
                                        {mobileHomeSections}
                                    </View>
                                </View>
                            );
                            isFirstMobileHomePage = false;
                        
                        pages.push(
                            <Page key={`page-${pages.length}`} size='A4' style={styles.page}>
                                {currentPageSections}
                                <Text style={styles.pageNumber} render={({pageNumber, totalPages}) => `Page ${pageNumber} of ${totalPages}`} />
                            </Page>
                        );
                        currentPageSections = [];
                        mobileHomeSections = [];
                        currentPageHeight = 0;
                        currentPageHeightWithSection = sectionTitleHeight;
                    }

                    mobileHomeSections.push(
                        <View key={index} style={styles.section}>
                            <Text style={[styles.sectionTitle, {fontSize: 14, marginTop: 15, marginBottom: 8}]}>Mobile Home {index + 1}</Text>
                            <View style={styles.row}>
                                <Text style={styles.label}>Year:</Text>
                                <Text style={styles.value}>{mobileHome.year ?? 'N/A'}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Make:</Text>
                                <Text style={styles.value}>{mobileHome.make ?? 'N/A'}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Model:</Text>
                                <Text style={styles.value}>{mobileHome.model ?? 'N/A'}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Serial Number:</Text>
                                <Text style={styles.value}>{mobileHome.serialNumber ?? 'N/A'}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Parcel Number:</Text>
                                <Text style={styles.value}>{mobileHome.parcelNumber ?? 'N/A'}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Decal:</Text>
                                <Text style={styles.value}>{mobileHome.decal ?? 'N/A'}</Text>
                            </View>
                            {
                                mobileHome.lienRetirement &&
                                    <>
                                        <Text style={[styles.sectionTitle, {fontSize: 12, marginTop: 10, marginBottom: 5}]}>Lien Retirement</Text>
                                        <View style={styles.row}>
                                            <Text style={styles.label}>Recorded Date:</Text>
                                            <Text style={styles.value}>{formatDate(mobileHome.lienRetirement.recordedDate)}</Text>
                                        </View>
                                        <View style={styles.row}>
                                            <Text style={styles.label}>Book:</Text>
                                            <Text style={styles.value}>{mobileHome.lienRetirement.book ?? 'N/A'}</Text>
                                        </View>
                                        <View style={styles.row}>
                                            <Text style={styles.label}>Page:</Text>
                                            <Text style={styles.value}>{mobileHome.lienRetirement.page ?? 'N/A'}</Text>
                                        </View>
                                    </>
                            }
                            {
                                mobileHome.taxYears && mobileHome.taxYears.length > 0 &&
                                    <>
                                        <Text style={[styles.sectionTitle, {fontSize: 12, marginTop: 10, marginBottom: 5}]}>Mobile Home Tax Years</Text>
                                        <View style={styles.table}>
                                            <View style={styles.tableHeader}>
                                                <Text style={styles.tableHeaderCell}>Year</Text>
                                                <Text style={styles.tableHeaderCell}>Amount Owed</Text>
                                                <Text style={styles.tableHeaderCell}>Amount Paid</Text>
                                            </View>
                                            {
                                                mobileHome.taxYears?.map((taxYear: MobileHomeTaxYear, taxIndex: number) => (
                                                    <View key={taxIndex} style={styles.tableRow}>
                                                        <Text style={styles.tableCell}>{taxYear.year ?? 'N/A'}</Text>
                                                        <Text style={styles.tableCell}>{formatCurrency(taxYear.amountOwed)}</Text>
                                                        <Text style={styles.tableCell}>{formatCurrency(taxYear.amountPaid)}</Text>
                                                    </View>
                                                ))
                                            }
                                        </View>
                                    </>
                            }
                        </View>
                    );
                    
                    currentPageHeightWithSection += individualHeight;
                });

                if (mobileHomeSections.length > 0) {
                    currentPageSections.push(
                        <View style={styles.sectionWrapper}>
                            <View style={styles.sectionContainer}>
                                {isFirstMobileHomePage && <Text style={styles.sectionTitle}>Mobile Homes</Text>}
                                {mobileHomeSections}
                            </View>
                        </View>
                    );
                    currentPageHeight = currentPageHeightWithSection - sectionTitleHeight;
                }
            } else if (key === 'mortgages' && hasMortgages) {
                const mortgages = props.titleSummaryRequest.mortgages ?? [];
                const sectionTitleHeight = 50;
                let mortgageSections: JSX.Element[] = [];
                let currentPageHeightWithSection = currentPageHeight + sectionTitleHeight;
                let isFirstMortgagePage = true;

                mortgages.forEach((mortgage, index) => {
                    const individualHeight = calculateIndividualMortgageHeight(mortgage);
                    
                    if (currentPageHeightWithSection + individualHeight > pageBreakThreshold) {
                        if (mortgageSections.length > 0) 
                            currentPageSections.push(
                                <View style={styles.sectionWrapper}>
                                    <View style={styles.sectionContainer}>
                                        {isFirstMortgagePage && <Text style={styles.sectionTitle}>Mortgages</Text>}
                                        {mortgageSections}
                                    </View>
                                </View>
                            );

                            isFirstMortgagePage = false;
    
                        pages.push(
                            <Page key={`page-${pages.length}`} size='A4' style={styles.page}>
                                {currentPageSections}
                                <Text style={styles.pageNumber} render={({pageNumber, totalPages}) => `Page ${pageNumber} of ${totalPages}`} />
                            </Page>
                        );

                        currentPageSections = [];
                        mortgageSections = [];
                        currentPageHeight = 0;
                        currentPageHeightWithSection = sectionTitleHeight;
                    }

                    mortgageSections.push(
                        <View key={index} style={styles.section}>
                            <Text style={[styles.sectionTitle, {fontSize: 14, marginTop: 15, marginBottom: 8}]}>Mortgage {index + 1}</Text>
                            <View style={styles.row}>
                                <Text style={styles.label}>Lender:</Text>
                                <Text style={styles.value}>{mortgage.lender ?? 'N/A'}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Amount:</Text>
                                <Text style={styles.value}>{formatCurrency(mortgage.amount)}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Recorded Date:</Text>
                                <Text style={styles.value}>{formatDate(mortgage.recordedDate)}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Book:</Text>
                                <Text style={styles.value}>{mortgage.book ?? 'N/A'}</Text>
                            </View>
                            <View style={styles.row}>
                                <Text style={styles.label}>Page:</Text>
                                <Text style={styles.value}>{mortgage.page ?? 'N/A'}</Text>
                            </View>
                            {
                                mortgage.assignments && mortgage.assignments.length > 0 &&
                                    <>
                                        <Text style={[styles.sectionTitle, {fontSize: 12, marginTop: 10, marginBottom: 5}]}>Assignments</Text>
                                        <View style={styles.table}>
                                            <View style={styles.tableHeader}>
                                                <Text style={styles.tableHeaderCell}>Lender</Text>
                                                <Text style={styles.tableHeaderCell}>Recorded</Text>
                                                <Text style={styles.tableHeaderCell}>Book</Text>
                                                <Text style={styles.tableHeaderCell}>Page</Text>
                                            </View>
                                            {
                                                mortgage.assignments?.map((assignment: Assignment, assignmentIndex: number) => (
                                                    <View key={assignmentIndex} style={styles.tableRow}>
                                                        <Text style={styles.tableCell}>{assignment.lender ?? 'N/A'}</Text>
                                                        <Text style={styles.tableCell}>{formatDate(assignment.recordedDate)}</Text>
                                                        <Text style={styles.tableCell}>{assignment.book ?? 'N/A'}</Text>
                                                        <Text style={styles.tableCell}>{assignment.page ?? 'N/A'}</Text>
                                                    </View>
                                                ))
                                            }
                                        </View>
                                </>
                            }
                        </View>
                    )
                    currentPageHeightWithSection += individualHeight;
                });

                if (mortgageSections.length > 0) {
                    currentPageSections.push(
                        <View style={styles.sectionWrapper}>
                            <View style={styles.sectionContainer}>
                                {isFirstMortgagePage && <Text style={styles.sectionTitle}>Mortgages</Text>}
                                {mortgageSections}
                            </View>
                        </View>
                    );
                    currentPageHeight = currentPageHeightWithSection - sectionTitleHeight;
                }
            } else {
                if (currentPageHeight + estimatedHeight > pageBreakThreshold && currentPageSections.length > 0) {
                    pages.push(
                        <Page key={`page-${pages.length}`} size='A4' style={styles.page}>
                            {currentPageSections}
                            <Text style={styles.pageNumber} render={({pageNumber, totalPages}) => `Page ${pageNumber} of ${totalPages}`} />
                        </Page>
                    );
                    currentPageSections = [];
                    currentPageHeight = 0;
                }
                
                currentPageSections.push(section);
                currentPageHeight += estimatedHeight;
            }
        });

        if (currentPageSections.length > 0) 
            pages.push(
                <Page key={`page-${pages.length}`} size='A4' style={styles.page}>
                    {currentPageSections}
                    <Text style={styles.pageNumber} render={({pageNumber, totalPages}) => `Page ${pageNumber} of ${totalPages}`} />
                </Page>
            );

        return <>{pages}</>;
    };

    return (
        <Document>
            {renderMainPage()}
            {renderAdditionalPages()}
        </Document>
    );
};

export default TitleSummaryReportDocument;
